import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import GoldSectionLayout from "../components/GoldSectionLayout";
import GoldLayerRenderer from "../components/GoldLayerRenderer";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sparkles, Database, TrendingUp, Target, Percent, Shield, Clock,
  Activity, Flag, MapPin, CloudSun, CalendarClock, Brain, Layers,
  Users, LineChart, Zap, Award, Trophy, Eye,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LAYERS = [
  { id: 1, title: "Data Acquisition", icon: Database, path: "/api/gold/layer/1/acquisition", args: [] },
  { id: 2, title: "League Intelligence", icon: Trophy, path: "/api/gold/layer/2/league/{league_id}", args: ["league_id"] },
  { id: 3, title: "Team DNA", icon: Eye, path: "/api/gold/layer/3/team/{team_id}", args: ["team_id"] },
  { id: 4, title: "Shot Intelligence", icon: Target, path: "/api/gold/layer/4/team/{team_id}/shots", args: ["team_id"] },
  { id: 5, title: "Shot Conversion", icon: Percent, path: "/api/gold/layer/5/team/{team_id}/conversion", args: ["team_id"] },
  { id: 6, title: "Defensive Shots", icon: Shield, path: "/api/gold/layer/6/team/{team_id}/defensive-shots", args: ["team_id"] },
  { id: 7, title: "Time Intelligence", icon: Clock, path: "/api/gold/layer/7/team/{team_id}/time", args: ["team_id"] },
  { id: 8, title: "Tactical", icon: Activity, path: "/api/gold/layer/8/team/{team_id}/tactical", args: ["team_id"] },
  { id: 9, title: "Referee", icon: Flag, path: "/api/gold/layer/9/referee/{referee}", args: ["referee"] },
  { id: 10, title: "Venue", icon: MapPin, path: "/api/gold/layer/10/venue/{venue}", args: ["venue"] },
  { id: 11, title: "Weather", icon: CloudSun, path: "/api/gold/layer/11/weather/{fixture_id}", args: ["fixture_id"] },
  { id: 12, title: "Schedule / Fatigue", icon: CalendarClock, path: "/api/gold/layer/12/schedule/{team_id}", args: ["team_id"] },
  { id: 13, title: "Psychological", icon: Brain, path: "/api/gold/layer/13/psychological/{team_id}", args: ["team_id"] },
  { id: 14, title: "League Patterns", icon: Layers, path: "/api/gold/layer/14/patterns/league/{league_id}", args: ["league_id"] },
  { id: 15, title: "Team Patterns", icon: Layers, path: "/api/gold/layer/15/patterns/team/{team_id}", args: ["team_id"] },
  { id: 16, title: "Player Synergy", icon: Users, path: "/api/gold/layer/16/synergy/{team_id}", args: ["team_id"] },
  { id: 17, title: "Market Intelligence", icon: LineChart, path: "/api/gold/layer/17/market/{fixture_id}", args: ["fixture_id"] },
  { id: 18, title: "ML Ensemble", icon: Zap, path: "/api/gold/layer/18/ml/{fixture_id}", args: ["fixture_id"] },
  { id: 19, title: "Pattern Confidence", icon: TrendingUp, path: "/api/gold/layer/19/confidence", args: [] },
  { id: 20, title: "Ensemble Decision", icon: Award, path: "/api/gold/layer/20/ensemble-weights", args: [] },
  { id: "h2h", title: "H2H Deep Dive", icon: Users, path: "/api/gold/h2h/{team_a}/{team_b}", args: ["team_a", "team_b"] },
];

const LayerCard = ({ layer, inputs, setInputs, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const url = layer.path.replace(/{(\w+)}/g, (_, k) => inputs[k] || "");
  const canFetch = layer.args.every((a) => inputs[a] && String(inputs[a]).length > 0);

  const fetchData = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [url, canFetch, token]);

  useEffect(() => {
    if (layer.args.length === 0) fetchData();
  }, [fetchData, layer.args.length]);

  const Icon = layer.icon || Sparkles;
  return (
    <Card
      className="border-amber-500/20 bg-gradient-to-br from-black via-neutral-950 to-neutral-900"
      data-testid={`gold-layer-${layer.id}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-amber-300">
          <span className="flex items-center gap-2 text-sm font-bold tracking-wide">
            <Icon className="w-4 h-4" />
            <span className="font-mono text-xs text-amber-500/70">L{String(layer.id).padStart(2, "0")}</span>
            <span>{layer.title}</span>
          </span>
          {layer.args.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={fetchData}
              disabled={!canFetch || loading}
              data-testid={`gold-layer-${layer.id}-fetch`}
            >
              {loading ? "…" : "Load"}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {layer.args.length > 0 && (
          <div className="flex gap-2 mb-3">
            {layer.args.map((arg) => (
              <Input
                key={arg}
                type="text"
                placeholder={arg}
                value={inputs[arg] || ""}
                onChange={(e) => setInputs((prev) => ({ ...prev, [arg]: e.target.value }))}
                className="h-8 text-xs bg-black/40 border-amber-500/20 text-amber-100 placeholder:text-amber-500/40"
                data-testid={`gold-layer-${layer.id}-input-${arg}`}
              />
            ))}
          </div>
        )}
        {error && (
          <div className="text-xs text-red-400 font-mono" data-testid={`gold-layer-${layer.id}-error`}>
            {error}
          </div>
        )}
        {!data && !error && !loading && layer.args.length > 0 && (
          <div className="text-xs text-neutral-500 italic">
            Enter {layer.args.join(" + ")} above and click Load.
          </div>
        )}
        {loading && <div className="text-xs text-amber-400 animate-pulse">Loading…</div>}
        {data && (
          <div data-testid={`gold-layer-${layer.id}-data`}>
            <GoldLayerRenderer layerId={layer.id} data={data} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function GoldExpertPage() {
  const { token } = useAuth();
  const [inputs, setInputs] = useState({});

  return (
    <GoldSectionLayout
      title="Expert Mode"
      subtitle="Manual layer inspection — hit each of the 20 institutional layers directly with your own IDs."
      testid="gold-expert-page"
    >
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="gold-expert-grid">
        {LAYERS.map((layer) => (
          <LayerCard
            key={layer.id}
            layer={layer}
            inputs={inputs}
            setInputs={setInputs}
            token={token}
          />
        ))}
      </div>
    </GoldSectionLayout>
  );
}
